import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
export interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}
interface InteractiveQuizProps {
  questions: QuizQuestion[];
  onComplete?: (score: number) => void;
}
export const InteractiveQuiz: React.FC<InteractiveQuizProps> = ({
  questions,
  onComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const handleSelect = (index: number) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
    setIsAnswered(true);
    if (index === questions[currentIndex].correctAnswer) {
      setScore(s => s + 1);
    }
  };
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(c => c + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      setIsFinished(true);
      if (onComplete) onComplete(score);
    }
  };
  if (isFinished) {
    return <div className="bg-white ink-border p-8 rounded-lg text-center shadow-sm" data-id="element-51">
        <h3 className="text-2xl font-serif text-gold-dark mb-4" data-id="element-52">测试完成</h3>
        <p className="text-lg mb-6 text-ink" data-id="element-53">
          你的得分:{' '}
          <span className="text-gold-dark font-bold text-2xl" data-id="element-54">{score}</span> /{' '}
          {questions.length}
        </p>
        <button onClick={() => {
        setCurrentIndex(0);
        setScore(0);
        setIsFinished(false);
        setSelectedAnswer(null);
        setIsAnswered(false);
      }} className="btn-secondary" data-id="element-55">
          重新测试
        </button>
      </div>;
  }
  const currentQ = questions[currentIndex];
  return <div className="bg-white ink-border p-6 md:p-8 rounded-lg max-w-2xl mx-auto shadow-sm" data-id="element-56">
      <div className="flex justify-between items-center mb-6 text-sm text-gold-dark/60 font-medium" data-id="element-57">
        <span data-id="element-58">随堂测试</span>
        <span data-id="element-59">
          {currentIndex + 1} / {questions.length}
        </span>
      </div>

      <h3 className="text-xl font-serif mb-6 leading-relaxed text-ink" data-id="element-60">
        {currentQ.question}
      </h3>

      <div className="space-y-3" data-id="element-61">
        {currentQ.options.map((option, idx) => {
        let btnClass = 'w-full text-left p-4 rounded border transition-all duration-300 font-medium ';
        if (!isAnswered) {
          btnClass += 'border-paper-dark bg-paper hover:border-gold/50 hover:bg-white text-ink';
        } else {
          if (idx === currentQ.correctAnswer) {
            btnClass += 'border-jade bg-jade/10 text-jade';
          } else if (idx === selectedAnswer) {
            btnClass += 'border-cinnabar bg-cinnabar/10 text-cinnabar';
          } else {
            btnClass += 'border-paper-dark bg-paper opacity-50 text-ink-light';
          }
        }
        return <button key={idx} onClick={() => handleSelect(idx)} disabled={isAnswered} className={btnClass} data-id="element-62">
              <div className="flex justify-between items-center" data-id="element-63">
                <span data-id="element-64">{option}</span>
                {isAnswered && idx === currentQ.correctAnswer && <CheckCircle2 className="w-5 h-5 text-jade" data-id="element-65" />}
                {isAnswered && idx === selectedAnswer && idx !== currentQ.correctAnswer && <XCircle className="w-5 h-5 text-cinnabar" data-id="element-66" />}
              </div>
            </button>;
      })}
      </div>

      <AnimatePresence data-id="element-67">
        {isAnswered && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="mt-6 pt-6 border-t border-paper-dark" data-id="element-68">
            <p className="text-ink-light mb-6 leading-relaxed bg-paper p-4 rounded-md" data-id="element-69">
              <span className="text-gold-dark font-bold mr-2" data-id="element-70">解析:</span>
              {currentQ.explanation}
            </p>
            <button onClick={handleNext} className="btn-primary w-full" data-id="element-71">
              {currentIndex < questions.length - 1 ? '下一题' : '查看结果'}
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
};